import moment from "moment";

export const getCourseFinalExam = course => {
  if (!course) return null;
  const { tests } = course;
  if (Array.isArray(tests)) {
    const finalExam = tests[0];
    return finalExam || null;
  }
  return null;
}

export const getCourseLastAttempt = (course) => {
  if (course && course !== null) {
    const { tests } = course;
    if (Array.isArray(tests)) {
      const finalExam = tests[0];
      if (finalExam) {
        const { attempts } = finalExam;
        if (Array.isArray(attempts)) {
          let sortedAttempts = attempts.sort((a, b) =>
            moment(a.createdAt).isAfter(moment(b.createdAt)) ? -1 : 1
          );
          const lastAttempt = sortedAttempts[0];
          if (lastAttempt) {
            return lastAttempt;
          }
        }
      }
    }
  }
};

export const getAccess = (course) => {
  const { course_accesses } = course;
  if (Array.isArray(course_accesses)) {
    const currentAccess = course_accesses[0];
    return currentAccess;
  }
};

export const getAccessProduct = (course_access) => {
  if (!course_access) return null;
  const { purchase } = course_access;
  if (!purchase) return null;
  return purchase.product || null;
};

// Legacy access: an attempt already exists for this course's final exam, so the
// user is grandfathered into presenting the exam on this course
export const hasFinalExamAttempt = (course) => {
  if (!course || !Array.isArray(course.tests)) return false;
  return course.tests.some(
    (test) => Array.isArray(test.attempts) && test.attempts.length > 0
  );
};

// Live courses require the user to pick which course their final exam is on
export const mustChooseFinalExam = (course) => {
  if (!course) return false;
  if (hasFinalExamAttempt(course)) return false;
  const { course_access } = course;
  if (!course_access || course_access.chose_final_exam) return false;
  const product = getAccessProduct(course_access);
  return product !== null && product.mode === "live";
};

// A live purchase grants access to several courses but only one final exam,
// so the choice is made once across every access from that same purchase
export const hasChosenFinalExam = (course_accesses, purchase_id) => {
  if (!Array.isArray(course_accesses) || !purchase_id) return false;
  return course_accesses.some(
    (course_access) =>
      parseInt(course_access.purchase_id) === parseInt(purchase_id) &&
      course_access.chose_final_exam
  );
};

// The chosen discipline is stored as the course name on the access that chose it
export const getChosenFinalExam = (course_accesses, purchase_id) => {
  if (!Array.isArray(course_accesses) || !purchase_id) return null;
  const chosen = course_accesses.find(
    (course_access) =>
      parseInt(course_access.purchase_id) === parseInt(purchase_id) &&
      course_access.chose_final_exam
  );
  return chosen ? chosen.final_exam : null;
};

// The exam is only chosen once every course of the purchase has its sections done
export const hasCompletedSections = (mycourses, accesses) => {
  if (!Array.isArray(mycourses) || !Array.isArray(accesses)) return false;
  if (accesses.length === 0) return false;
  return accesses.every(({ course_id }) => {
    const mycourse = mycourses.find(
      (current) => parseInt(current.course_id) === parseInt(course_id)
    );
    return mycourse ? mycourse.completed_sections === true : false;
  });
};

// The exam can be presented on any of the courses the live purchase granted
export const getPurchaseAccesses = (course_accesses, purchase_id) => {
  if (!Array.isArray(course_accesses) || !purchase_id) return [];
  return course_accesses.filter(
    (course_access) =>
      parseInt(course_access.purchase_id) === parseInt(purchase_id) &&
      course_access.course
  );
};

export const getCapacity = (course, location) => {
  let capacity = 0;
  const { course_products } = course;
  if (Array.isArray(course_products)) {
    course_products.forEach(({ product }) => {
      if (
        (location && product.location === location) ||
        location !== "studio"
      ) {
        capacity += parseInt(product.capacity);
      }
    });
  }
  return capacity;
};

export const isCompleted = (course, completed_courses) => {
  if (Array.isArray(completed_courses)) {
    return (
      completed_courses.find(
        ({ course_id }) => parseInt(course_id) === parseInt(course.course_id)
      ) !== undefined
    );
  }
};

export const getPrecondition = (course, completed_courses) => {
  if (course.course_id === 1) return true;
  if (Array.isArray(completed_courses)) {
    const currentCourse = completed_courses.find(
      ({ course_id }) => course_id === course.course_id - 1
    );
    return currentCourse !== undefined;
  }
};

export const getPrice = (course) => {
  let minPrice;
  const { course_products } = course;
  if (Array.isArray(course_products)) {
    course_products
      .filter(({ course_id }) => course_id === course.course_id)
      .forEach(({ product }) => {
        if (!minPrice) {
          minPrice = product.price;
        } else if (product.price < minPrice) {
          minPrice = product.price;
        }
      });
  }
  return minPrice;
};

export const getStartEndDate = (course, location) => {
  const { course_products } = course;
  if (Array.isArray(course_products)) {
    let minDate;
    let endDate;
    course_products.forEach(({ product }) => {
      if ((location && product.location === location) || !location) {
        if (product.start_date !== null) {
          if (!minDate) {
            minDate = product.start_date;
            endDate = product.end_date;
          } else if (moment(product.start_date).isBefore(minDate)) {
            minDate = product.start_date;
            endDate = product.end_date;
          }
        }
      }
    });
    return { startDate: minDate, endDate };
  }
  return { startDate: null, endDate: null };
};

// Central Mexico is UTC-6 year-round (Mexico abolished DST in 2022).
// The grace deadline is the end of day in Central time, so we offset the
// UTC end-of-day by 6 hours to reach midnight Central.
const CENTRAL_UTC_OFFSET_HOURS = 6;

// End of day (23:59 CDMX) for a given moment, expressed as a UTC instant.
const endOfCentralDay = (m) =>
  m.clone().endOf("day").add(CENTRAL_UTC_OFFSET_HOURS, "hours");

// Last day videos/modules can be viewed: expiration_date, 23:59 CDMX.
export const getVideoDeadline = (course_access) =>
  endOfCentralDay(moment.utc(course_access.expiration_date));

// Last day the final exam can be uploaded: expiration_date + 7 days, 23:59 CDMX.
export const getExamDeadline = (course_access) =>
  endOfCentralDay(moment.utc(course_access.expiration_date).add(7, "days"));

// Video/module access has ended (user may still be in the exam-upload period).
export const isVideoAccessExpired = (course_access) => {
  return getVideoDeadline(course_access).isBefore(moment.utc());
};

// Access fully expired: past the exam-upload deadline too.
export const isAccessExpired = (course_access) => {
  return getExamDeadline(course_access).isBefore(moment.utc());
};

export const isExamAccessExpired = (course_access) => {
  return getExamDeadline(course_access).isBefore(moment.utc());
};
